import { TFunction } from 'i18next';

import { CONSIGNMENT_STATUS, ConsignmentStatusCode } from '@/entities/consignments';
import type { SelectOption } from '@/shared/ui';

export const getMetricOptions = (t: TFunction): SelectOption[] => [
  { value: 'W', label: t('hover.weight', 'Weight') },
  { value: 'V', label: t('hover.volume', 'Volume') },
  { value: 'VA', label: t('requestline.insuranceValue.label', 'Total Value') },
  { value: 'C', label: t('cmrlist.title', 'Consignments') },
  { value: 'L', label: t('welcome.num.conslines.label', 'Number of consignment lines') },
  { value: 'D', label: t('requestline.isDangerous.label', 'Dangerous') }
];

export const getGroupByOptions = (t: TFunction): SelectOption[] => [
  { value: 'D', label: t('gen.date.label', 'Date') },
  { value: 'W', label: t('gen.week.label', 'Week') },
  { value: 'L', label: t('location.name.label', 'Location') },
  { value: 'S', label: t('subregion.name.label', 'Subregion') },
  { value: 'R', label: t('region.name.label', 'Region') },
  { value: 'U', label: t('org.name.label', 'Service User') },
  { value: 'T', label: t('org.type.label', 'Service User Type') },
  { value: 'P', label: t('product.description.label', 'Product Category') }
];

export const getDateRangeOptions = (t: TFunction): SelectOption[] => [
  { value: 'LW', label: t('filter.period.lastWeek', 'Last Week') },
  { value: 'L2W', label: t('filter.period.last2Weeks', 'Last 2 Weeks') },
  { value: 'LM', label: t('filter.period.lastMonth', 'Last Month') },
  { value: 'L2M', label: t('filter.period.last2Months', 'Last 2 Months') },
  { value: 'L6M', label: t('filter.period.last6Months', 'Last 6 Months') },
  { value: 'LY', label: t('filter.period.lastYear', 'Last Year') },
  { value: 'FE', label: t('filter.period.allTime', 'All Time') },
  { value: 'CR', label: t('filter.period.customRange', 'Custom Range') }
];

export const getStatusOptions = (t: TFunction): SelectOption[] => [
  { value: 'DI', label: t('welcome.dispatched.label', 'Dispatched') },
  { value: 'RC', label: t('welcome.received.label', 'Received') }
];

export const getTransportGroupOptions = (t: TFunction): SelectOption[] => {
  const origin = t('welcome.origin.label', 'Origin');
  const destination = t('request.destinationLocation.label', 'Destination');
  const subregion = t('subregion.name.label', 'Subregion');
  const region = t('region.name.label', 'Region');

  return [
    { value: 'D', label: t('gen.date.label', 'Date') },
    { value: 'OL', label: t('journey.origin.label', 'Origin Location') },
    { value: 'OS', label: `${origin} ${subregion}` },
    { value: 'OR', label: `${origin} ${region}` },
    { value: 'DL', label: destination },
    { value: 'DS', label: `${destination} ${subregion}` },
    { value: 'DR', label: `${destination} ${region}` },
    { value: 'SO', label: t('org.name.label', 'Service User') },
    { value: 'SOT', label: t('org.type.label', 'Service User Type') },
    { value: 'P', label: t('product.description.label', 'Product Category') }
  ];
};

/**
 * Break-down (stacking) dimension for the transport chart. Values and order
 * mirror the legacy JSF dropdown (`welcome.xhtml`, `data-opname="trmode"`) and
 * the API enum: `CO` groups by origin-destination pair (corridor), `CD` by
 * cargo condition (Lost / Sane / Damaged). The API has no commodity break-down.
 */
export const getBreakdownOptions = (t: TFunction): SelectOption[] => [
  { value: 'NO', label: t('gen.none.label', 'None') },
  { value: 'OL', label: t('journey.origin.label', 'Origin Location') },
  { value: 'DL', label: t('request.destinationLocation.label', 'Destination') },
  { value: 'CO', label: t('welcome.corridor.label', 'Corridor') },
  { value: 'CD', label: t('dispatch.condition.label', 'Condition') }
];

export const getConsignmentsStatusOptions = (t: TFunction): SelectOption<ConsignmentStatusCode | ''>[] => [
  { value: '', label: t('common.all', 'All') },
  { value: CONSIGNMENT_STATUS.AC, label: t('cmrlist.filters.accepted.label', 'Accepted') },
  { value: CONSIGNMENT_STATUS.CA, label: t('cmrlist.filters.cancelled.label', 'Cancelled') },
  { value: CONSIGNMENT_STATUS.CL, label: t('cmrlist.filters.closed.label', 'Closed') },
  { value: CONSIGNMENT_STATUS.IP, label: t('cmrlist.filters.inProgress.label', 'In Progress') },
  { value: CONSIGNMENT_STATUS.NR, label: t('cmrlist.filters.new.label', 'New') },
  { value: CONSIGNMENT_STATUS.OH, label: t('cmrlist.filters.onHold.label', 'On Hold') }
];

export const getTransportModeOptions = (
  t: TFunction,
  vehicleCategories: { id: number; description: string }[]
): SelectOption[] => [
  { value: 'NO', label: t('gen.none.label', 'None') },
  ...vehicleCategories.map((vc) => ({ value: String(vc.id), label: t(`vehicleCategory.${vc.id}`, vc.description) }))
];
